// Angular
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
// RxJS
import {AuthService} from '../../../../../core/auth/_services/auth.service';
import {UserModel} from '../../../../../core/auth/_models/user.model';
import {environment} from '../../../../../../environments';
import {debounceTime, filter, switchMap} from "rxjs/operators";
import {Subject} from 'rxjs';
import { MatDialog } from "@angular/material/dialog";
import {DownloadManualComponent} from '../download-manual/download-manual.component';
import packageInfo from '../../../../../../../package.json';
import { PeriodItems } from '@app/enums/periods.enum';
import { SelectDialogComponent, SelectDialogData } from '@app/views/models/common-dialogs/select-dialog/select-dialog.component';
import { ISelectionItem } from '@app/models/selection-item';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@app/services/notification.service';
import { DatasourcesService } from '@app/services/datasources.service';
import { PermissionType } from '@app/core/auth/enums/permission.enum';
const version = packageInfo.version;

@Component({
	selector: 'kt-user-profile3',
	templateUrl: './user-profile3.component.html',
})
export class UserProfile3Component implements OnInit {

	periodItems: ISelectionItem[] = PeriodItems;

	// Public properties
	user: UserModel;
	canViewPeriodicities: boolean = false;
	canPeriodicityConfigInbox: boolean = false;
	canViewPeriodicityConfig: boolean = false;
	canAddEditDeletePeriodicityConfig: boolean = false;
	canSurveyListAsLeader: boolean = false;

	@Input() avatar: boolean = true;
	@Input() greeting: boolean = true;
	@Input() badge: boolean;
	@Input() icon: boolean;
	background: string;

	// Listener for the email alert checkbox
	changeReceiveEmails$ = new Subject<boolean>();
	changeReceiveCDIWarnings$ = new Subject<boolean>();
	loadingEmail: boolean;
	version;
	/**
	 * Component constructor
	 *
	 * @param authService: AuthService
	 */
	constructor(
		private authService: AuthService,
		private changeDetection: ChangeDetectorRef,
		private matDialogs: MatDialog,
		private translate: TranslateService,
		private notificationService: NotificationService,
		private dataSource: DatasourcesService
	) {
		this.background = environment.assets.profileComponenteBackground;
		this.version = version;
		this.canViewPeriodicities = this.authService.userHasPermission(PermissionType.FEC_Client_ViewConflictOfInterestsSurveyInbox);
		this.canPeriodicityConfigInbox = this.authService.userHasPermission(PermissionType.BOC_Client_PeriodicityConfigInbox);
		this.canViewPeriodicityConfig = this.authService.userHasPermission(PermissionType.BOC_Client_ViewPeriodicityConfig);
		this.canAddEditDeletePeriodicityConfig = this.authService.userHasPermission(PermissionType.BOC_Client_AddEditDeletePeriodicityConfig);
		this.canSurveyListAsLeader = this.authService.userHasPermission(PermissionType.CanSurveyListAsLeader);
	}

	getBackgroundImage() {
		return `url(${this.background})`; 
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.user = this.authService.currentUser;
		
		if(this.user.CDIWarningsPeriod === undefined){
			this.dataSource.getPeriodicityPreference().subscribe(response => {
				if(response !== null){
					this.user.CDIWarningsPeriod = response.periodicityId;
				}
			})
		}
		
		if(this.canViewPeriodicities 
			|| this.canPeriodicityConfigInbox
			|| this.canViewPeriodicityConfig 
			|| this.canAddEditDeletePeriodicityConfig
			|| this.canSurveyListAsLeader)
			  this.dataSource.getPeriodicities().subscribe(response => this.periodItems = response?.length ? response : this.periodItems);

		this.changeReceiveEmails$
			.pipe(
				debounceTime(500),
				// distinctUntilChanged(),
				switchMap(checked => {
					this.loadingEmail = true;
					this.changeDetection.detectChanges();
					return this.authService.changeReceiveAlerts(checked);
				}),
			)
			.subscribe({
				next: (checked) => {
					this.user.emailAlerts = checked;
					this.spinnerOff();
				},
				error: err => {
					console.log(err);
					this.spinnerOff();
				},
				complete: () => this.spinnerOff()
			});

		this.changeReceiveCDIWarnings$
			.pipe(
				debounceTime(500),
				// distinctUntilChanged(),
				switchMap(checked => {
					this.loadingEmail = true;
					this.changeDetection.detectChanges();
					return this.authService.changeReceiveCDIWarnings(checked);
				}),
			)
			.subscribe({
				next: (checked) => {
					this.user.CDIWarnings = checked;
					this.spinnerOff();
				},
				error: err => {
					console.log(err);
					this.spinnerOff();
				},
				complete: () => this.spinnerOff()
			});
	}

	spinnerOff(): void {
		this.loadingEmail = false;
		this.changeDetection.detectChanges();
	}

	editPeriodCDIWarnings(): void {
		this.user = this.authService.currentUser;
		if(!this.canViewPeriodicities) return;
		const selectDialogData: SelectDialogData = {
			items: JSON.parse(JSON.stringify([...this.periodItems])),
			selectedOption: this.user.CDIWarningsPeriod,
			placeholder: this.translate.instant('PERIOD-CONFIG.DIALOG-PLACEHOLDER'),
			translatePath: 'PERIOD-CONFIG.PERIODS'
		}
		this.matDialogs.open(SelectDialogComponent, {
            width: 'auto',
			minWidth: '350px',
            data: selectDialogData
		}).afterClosed()
		.pipe(
			filter(response => !!response),
			switchMap(response => {
				this.loadingEmail = true;
				this.changeDetection.detectChanges();
				return this.authService.AddOrChangeCDIWarningsPeriod(response)
			}))
			.subscribe({
				next: () => {
					this.notificationService.openSnackBar(this.translate.instant('PERIOD-CONFIG.PERIOD-UPDATED'));
					this.spinnerOff();
				},
				error: err => {
					console.log(err);
					this.spinnerOff();
				},
				complete: () => this.spinnerOff()
			});
	}

	/**
	 * Log out
	 */
	logout() {
		this.authService.logout();
	}

	downloadManual() {
		this.matDialogs.open(DownloadManualComponent);
	}

}
